// Components
import {
  Select,
  Stack,
  Text,
  Group,
  ScrollArea,
  Textarea,
  Button,
  Loader,
  Alert,
  Box,
  Paper,
  Divider,
  ActionIcon,
} from "@mantine/core";
import { Drawer } from "@benchmark/ui/components/atomic";
import {
  IconMessage,
  IconSend,
  IconAlertCircle,
  IconPhone,
  IconCheck,
  IconUser,
  IconSettings,
} from "@tabler/icons-react";
import { SMSMessage } from "./SMSMessage";
import { CallItem } from "./CallItem";

// Hooks
import {
  useState,
  useId,
  useMemo,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  startTransition,
} from "react";
import { useSMSThread } from "@/hooks/domain/useSMSThread";
import { useSendSMS } from "@/hooks/domain/useSendSMS";
import { useSMSMessageStatus } from "@/hooks/domain/useSMSMessageStatus";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePatientAssignedClinician } from "@/hooks/domain/usePatientAssignedClinician";
import { useSMSComposer } from "@/hooks/domain/useSMSComposer";
import { useCurrentUser } from "@/app/auth/CurrentUserContext";
import { TelemetryActions, trackAction } from "@/app/boot/telemetry";
import {
  SMS_TEMPLATES,
  SMS_TEMPLATE_CATALOG_VERSION,
  getSMSPractitionerFirstName,
  getSMSPlaceholders,
  renderSMSTemplate,
  type SMSTemplateKey,
} from "./sms-templates";

// Utils
import { notifications } from "@mantine/notifications";
import { showErrorNotification } from "@benchmark/ui/utils";
import {
  getActivePatientTelecom,
  getPatientDisplayName,
} from "@benchmark/core/resolvers";
import { extractPractitionerRolePhoneNumbers } from "@benchmark/core/utils";
import { isPhoneCall } from "@/utils/fhir/communication";

// Events
import { globalEventEmitter, DomainEventNames } from "@benchmark/core/events";

// Types
import type { Communication, Identifier, Patient } from "@medplum/fhirtypes";
import type { CommunicationCreatedEvent } from "@benchmark/core/events";
import type { SMSThreadDrawerProps } from "@/types/domain/messaging";

// Styles
import styles from "./SMSThreadDrawer.module.css";

/**
 * Extracts the patient's current (not ended) phone numbers for SMS recipient selection.
 * @agentic connections
 */
function getPatientPhoneNumbers(
  patient: Patient | undefined,
): Array<{ value: string; label: string }> {
  if (!patient) {
    return [];
  }

  return (getActivePatientTelecom(patient) ?? [])
    .filter(
      (t): t is typeof t & { value: string } =>
        t.system === "phone" && Boolean(t.value),
    )
    .map((t, index) => ({
      value: t.value,
      label: `${t.value}${t.use ? ` (${t.use})` : ""}${index === 0 ? " - Primary" : ""}`,
    }));
}

/**
 * Extracts Dialpad conversation ID from Communication identifiers.
 * @agentic connections
 */
function getConversationId(communication: Communication): string | undefined {
  const identifier = communication.identifier?.find(
    (id: Identifier) => id.system === "dialpad:conversationId",
  );
  return identifier?.value;
}

/**
 * Dark-mode input styling shared by the phone-number `Select`s and the
 * message `Textarea` — the drawer is always dark (see `colorScheme="dark"`
 * above), so this is unconditional, not a light/dark branch.
 *
 * The visible `input` sits inline inside the Drawer, so it can safely
 * reference the scoped `--drawer-dark-*` custom properties. `Select`'s
 * dropdown/option list is a different story: Mantine's `Combobox` portals it
 * to `document.body` by default (`withinPortal: true`), which breaks custom
 * property *inheritance* (the portaled DOM subtree is no longer a descendant
 * of the `.drawerContentDark`-scoped element that defines those variables).
 * `dropdown`/`option` are handled separately via CSS classNames with literal
 * colors — see `.darkSelectDropdown`/`.darkSelectOption` below, which apply
 * correctly regardless of portal position since plain CSS class rules don't
 * depend on DOM ancestry the way custom-property inheritance does.
 * @agentic connections
 */
const darkInputStyles = {
  input: {
    backgroundColor: "rgba(255,255,255,0.06)",
    borderColor: "var(--drawer-dark-surface-border)",
    color: "var(--drawer-dark-text)",
  },
};

/**
 * How close (in px) to the bottom the viewport must be for the thread to keep
 * auto-following new messages. Beyond this, the user is considered to be
 * reading history, so incoming/polled messages must not yank them down.
 * @agentic connections
 */
const STICK_TO_BOTTOM_THRESHOLD_PX = 80;

/**
 * Props for the per-patient SMS thread body. `patientId` is always defined
 * here (the shell only renders content once a patient has been loaded), and
 * this component is keyed by it so a patient switch remounts a clean instance.
 * @agentic connections
 */
type SMSThreadContentProps = {
  patientId: string;
  opened: boolean;
  patient: Patient | undefined;
  isLoadingPatient: boolean;
  onMessageSent?: (communication: Communication) => void;
};

/**
 * Per-patient SMS thread body (context selectors, message list, compose bar).
 * Owns all per-patient state and data hooks. Because the shell keys this
 * component by `patientId`, switching patients unmounts this instance and
 * mounts a fresh one — resetting compose fields, optimistic messages, and the
 * thread so nothing bleeds from the previous patient (and an in-flight fetch
 * for the old patient can't overwrite the new thread, since its instance is
 * gone).
 * @agentic connections
 */
const SMSThreadContent = ({
  patientId,
  opened,
  patient,
  isLoadingPatient,
  onMessageSent,
}: SMSThreadContentProps) => {
  // Get assigned clinician's PractitionerRole from patient's CareTeam
  const {
    practitionerRole: assignedClinicianRole,
    practitioner: assignedClinician,
    isLoading: isLoadingClinician,
    error: clinicianError,
    isLoadingPractitioner: isLoadingClinicianName,
    practitionerError: clinicianNameError,
    refetch: refetchClinician,
  } = usePatientAssignedClinician(patientId, { enabled: opened });
  const currentUser = useCurrentUser();

  // Get assigned clinician's phone numbers using standard utility
  const practitionerPhoneNumbers = useMemo(() => {
    if (!assignedClinicianRole) return [];
    return extractPractitionerRolePhoneNumbers(assignedClinicianRole);
  }, [assignedClinicianRole]);

  // Fetch SMS messages and phone calls with polling when drawer is open
  const {
    messages,
    isLoading: isLoadingMessages,
    error,
    loadOlderMessages,
    isLoadingOlder,
    hasMoreOlder,
  } = useSMSThread(patientId, {
    enablePolling: opened,
    pollingInterval: 30000, // Poll every 30 seconds
  });

  // Send SMS hook
  const { sendSMS, isSending, error: sendError } = useSendSMS();

  // Local state
  const {
    text: messageText,
    provenance,
    textareaRef,
    textareaProps,
    replaceTemplate,
    reset: resetComposer,
    isComposing,
  } = useSMSComposer();
  const sendInFlightRef = useRef(false);
  const placeholders = getSMSPlaceholders(messageText);
  const templateLabelId = useId();
  const placeholderErrorId = useId();
  const surveyHintId = useId();
  const isSurveyDraft = SMS_TEMPLATES.some(
    (template) => template.key === provenance?.templateKey && template.isSurvey,
  );
  const [selectedToNumber, setSelectedToNumber] = useState<string>("");
  const [selectedFromNumber, setSelectedFromNumber] = useState<string>("");
  const [optimisticMessages, setOptimisticMessages] = useState<Communication[]>(
    [],
  );

  // Track pending message for targeted polling
  // This is the Dialpad messageId returned from the send-sms API
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);

  // Use targeted polling for the pending message (much more efficient!)
  // Instead of fetching 1000 messages every 30s, this polls for exactly 1 message
  // by its Dialpad messageId identifier every 2 seconds for up to 30 seconds
  const { isDelivered, communication: deliveredMessage } =
    useSMSMessageStatus(pendingMessageId);

  // Scroll management refs.
  // `viewportRef` is the ACTUAL scrollable node (Mantine ScrollArea wraps it);
  // the previously-attached root ref could not be scrolled.
  const viewportRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  // Whether the thread should keep auto-following the newest message. Starts
  // true (open pinned to bottom); flips false once the user scrolls up to read
  // history, so polls/inbound messages don't drag them back down.
  const stickToBottomRef = useRef(true);
  // Ensures the instant "pin to newest" runs once per mounted thread.
  const didInitialScrollRef = useRef(false);
  // Marks that the next message-list growth is an older-history PREPEND, so the
  // layout effect anchors the viewport instead of following the bottom.
  const isPrependingRef = useRef(false);
  // Viewport scrollHeight captured just before an older page is requested, used
  // to restore the read position after the batch prepends.
  const prevScrollHeightRef = useRef(0);
  // Id of the oldest rendered message (top of the ascending list). Used to
  // detect a genuine older-history PREPEND (oldest id changes) so the anchor
  // adjustment fires only on the prepend commit — not on a concurrent poll /
  // inbound / send that appends at the bottom while a fetch is in flight.
  const prevOldestIdRef = useRef<string | undefined>(undefined);
  // Id of the newest rendered message (bottom of the ascending list). Used to
  // fire bottom-follow only when a genuinely new newest message arrives — not
  // on the initial pin, a reopen, or an older-history prepend.
  const prevNewestIdRef = useRef<string | undefined>(undefined);

  // Extract phone numbers
  const phoneNumbers = useMemo(
    () => getPatientPhoneNumbers(patient),
    [patient],
  );

  // Set default phone numbers when patient/practitioner loads
  useEffect(() => {
    if (phoneNumbers.length > 0 && !selectedToNumber) {
      startTransition(() => {
        setSelectedToNumber(phoneNumbers[0].value);
      });
    }
  }, [phoneNumbers, selectedToNumber]);

  useEffect(() => {
    // Preserve an explicit selection through loading. Only a successful lookup
    // can confirm whether it is still valid; the send guard rejects stale values.
    if (isLoadingClinician || clinicianError || !opened) return;
    if (
      !practitionerPhoneNumbers.some(
        (number) => number.value === selectedFromNumber,
      )
    ) {
      const nextNumber = practitionerPhoneNumbers[0]?.value ?? "";
      if (nextNumber !== selectedFromNumber) {
        startTransition(() => setSelectedFromNumber(nextNumber));
      }
    }
  }, [
    practitionerPhoneNumbers,
    selectedFromNumber,
    isLoadingClinician,
    clinicianError,
    opened,
  ]);

  const hasValidSender =
    !isLoadingClinician &&
    !clinicianError &&
    practitionerPhoneNumbers.some(
      (number) => number.value === selectedFromNumber,
    );
  const hasValidRecipient =
    !isLoadingPatient &&
    phoneNumbers.some((number) => number.value === selectedToNumber);

  const isClinicianTemplateUnavailable =
    isLoadingClinician ||
    Boolean(clinicianError) ||
    isLoadingClinicianName ||
    Boolean(clinicianNameError);

  const selectTemplate = (key: SMSTemplateKey) => {
    if (sendInFlightRef.current || isSending || !hasValidRecipient) return;
    if (key !== "scheduled" && isClinicianTemplateUnavailable) return;
    const text = renderSMSTemplate(key, {
      senderName: getSMSPractitionerFirstName(currentUser.practitioner),
      clinicianName: getSMSPractitionerFirstName(assignedClinician),
    });
    replaceTemplate(text, {
      templateKey: key,
      catalogVersion: SMS_TEMPLATE_CATALOG_VERSION,
      renderedText: text,
    });
  };

  // Guards against acting twice on the same delivered messageId (idempotent
  // publish/removal) — see the delivery effect below.
  const handledMessageIdRef = useRef<string | null>(null);

  // Handle when targeted polling finds the delivered message
  useEffect(() => {
    if (!isDelivered || !deliveredMessage || !pendingMessageId) {
      return;
    }

    // Only reconcile when the found resource is actually the one we're waiting
    // on. Without this, a delivery result left over from a previous send (before
    // useSMSMessageStatus resets on a messageId change) could clear the wrong
    // optimistic bubble and publish a false CommunicationCreated.
    const deliveredMessageId = deliveredMessage.identifier?.find(
      (id: Identifier) => id.system === "dialpad:messageId",
    )?.value;
    if (deliveredMessageId !== pendingMessageId) {
      return;
    }

    // Idempotency: never publish/notify twice for the same messageId.
    if (handledMessageIdRef.current === pendingMessageId) {
      return;
    }
    handledMessageIdRef.current = pendingMessageId;

    // Remove the optimistic message that matches this messageId
    // Use startTransition to signal this is a non-urgent update
    startTransition(() => {
      setOptimisticMessages((prev) =>
        prev.filter((optMsg) => {
          const optMessageId = optMsg.identifier?.find(
            (id: Identifier) => id.system === "dialpad:messageId",
          )?.value;
          return optMessageId !== pendingMessageId;
        }),
      );

      // Clear the pending messageId
      setPendingMessageId(null);
    });

    // Bridge the webhook-created resource onto the in-process event bus so
    // subscribed views refresh without a hard reload: this drawer's own
    // thread (useSMSThread) and the patient timeline (usePatientTimeline)
    // both listen for CommunicationCreated filtered by patientId. The webhook
    // write is server-side and never reaches this emitter directly (see the
    // `mutation-refetch` rule A2), so we publish here, at the moment the
    // client discovers the real resource via targeted polling.
    if (deliveredMessage.id) {
      globalEventEmitter.publish<CommunicationCreatedEvent>(
        DomainEventNames.CommunicationCreated,
        {
          communicationId: deliveredMessage.id,
          communication: deliveredMessage,
          patientId,
        },
      );
    }

    // Show success notification
    notifications.show({
      title: "Message Delivered",
      message: "Your message was sent successfully",
      color: "green",
      icon: <IconCheck size={16} />,
    });
  }, [isDelivered, deliveredMessage, pendingMessageId, patientId]);

  // Combine real messages with optimistic messages
  const allMessages = useMemo(() => {
    // Filter out optimistic messages that have been replaced by real ones
    // First check by messageId (most reliable), then fall back to content matching
    const optimisticFiltered = optimisticMessages.filter((optMsg) => {
      // Get the messageId from the optimistic message
      const optMessageId = optMsg.identifier?.find(
        (id: Identifier) => id.system === "dialpad:messageId",
      )?.value;

      // Check if there's a real message with the same messageId
      if (optMessageId) {
        const hasRealVersionById = messages.some((realMsg) => {
          const realMessageId = realMsg.identifier?.find(
            (id: Identifier) => id.system === "dialpad:messageId",
          )?.value;
          return realMessageId === optMessageId;
        });
        if (hasRealVersionById) return false;
      }

      // Fall back to content + timestamp matching for messages without messageId
      const hasRealVersionByContent = messages.some((realMsg) => {
        const contentMatches =
          realMsg.payload?.[0]?.contentString ===
          optMsg.payload?.[0]?.contentString;
        const timeClose =
          realMsg.sent &&
          optMsg.sent &&
          Math.abs(
            new Date(realMsg.sent).getTime() - new Date(optMsg.sent).getTime(),
          ) < 5000;
        return contentMatches && timeClose;
      });
      return !hasRealVersionByContent;
    });

    // Combine and sort by sent date
    return [...messages, ...optimisticFiltered].sort((a, b) => {
      const dateA = new Date(a.sent || 0).getTime();
      const dateB = new Date(b.sent || 0).getTime();
      return dateA - dateB;
    });
  }, [messages, optimisticMessages]);

  // Track how close the viewport is to the bottom so auto-follow only happens
  // when the user is already there (not while reading older history).
  const handleScrollPositionChange = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const distanceFromBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    stickToBottomRef.current =
      distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD_PX;
  }, []);

  // Request the next older batch, capturing the current scroll height first so
  // the layout effect can anchor the read position once the batch prepends.
  const handleLoadOlder = useCallback(() => {
    // Synchronous re-entry guard: while a prepend is already pending (fetch in
    // flight), ignore repeated sentinel intersections so we don't overwrite the
    // captured baseline height. Also bail when there's no older history left.
    if (isPrependingRef.current || !hasMoreOlder) {
      return;
    }
    const viewport = viewportRef.current;
    // Only auto-load when the viewport is actually scrollable. If the whole
    // thread fits (scrollHeight <= clientHeight), the top sentinel stays
    // permanently intersecting and scrollTop can't move to anchor a prepend —
    // which would chain-fetch older pages the user never scrolled to. A
    // scrollable viewport is also the precondition for the scroll-up gesture
    // this services, so requiring it doesn't affect real usage.
    if (!viewport || viewport.scrollHeight <= viewport.clientHeight) {
      return;
    }
    prevScrollHeightRef.current = viewport.scrollHeight;
    isPrependingRef.current = true;
    void loadOlderMessages().then((fetched) => {
      // If no fetch was actually issued (no cursor / nothing older), disarm so
      // the next genuinely-new message isn't mistaken for a prepend.
      if (!fetched) {
        isPrependingRef.current = false;
      }
    });
  }, [loadOlderMessages, hasMoreOlder]);

  // Open pinned to the newest message: jump to the bottom instantly (before
  // paint) the first time content is present, so there's no visible top→bottom
  // scroll animation and no flash of the oldest message.
  useLayoutEffect(() => {
    if (!opened || didInitialScrollRef.current) return;
    const viewport = viewportRef.current;
    if (!viewport || allMessages.length === 0) return;
    viewport.scrollTop = viewport.scrollHeight;
    didInitialScrollRef.current = true;
    stickToBottomRef.current = true;
    // Seed the id trackers so the first paint isn't mistaken for a new-message
    // append (bottom-follow) or an older-history prepend (anchor).
    prevNewestIdRef.current = allMessages[allMessages.length - 1]?.id;
    prevOldestIdRef.current = allMessages[0]?.id;
  }, [opened, allMessages]);

  // The body stays mounted while the drawer closes (slide-out animation), so
  // reset the pin latch on close — reopening the same patient should land at
  // the newest message again rather than restoring a stale scroll position.
  useEffect(() => {
    if (!opened) {
      didInitialScrollRef.current = false;
      stickToBottomRef.current = true;
    }
  }, [opened]);

  // Preserve the read position when an older batch prepends: shift scrollTop by
  // the height the prepended messages added, so the message under the user's
  // eye stays put instead of jumping down. Keyed on the oldest message id
  // changing (a genuine prepend) rather than any `allMessages` change, so a
  // concurrent poll/inbound/send that appends at the bottom neither consumes
  // the pending flag nor mis-adjusts the scroll.
  //
  // Residual (accepted): if an append lands in the *same* React commit as the
  // prepend (both setStates batched in one tick — near-impossible across the
  // independent async sources here), the delta over-adjusts by the appended
  // row's height. Bounded to one message; eliminating it entirely would require
  // per-element scroll anchoring, which isn't justified by the risk.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const oldestId = allMessages[0]?.id;
    if (
      viewport &&
      isPrependingRef.current &&
      oldestId &&
      oldestId !== prevOldestIdRef.current
    ) {
      const delta = viewport.scrollHeight - prevScrollHeightRef.current;
      if (delta > 0) {
        viewport.scrollTop += delta;
      }
      isPrependingRef.current = false;
    }
    prevOldestIdRef.current = oldestId;
  }, [allMessages]);

  // Safety net: if an older-history fetch finishes without adding any messages
  // (e.g. an empty tail page), the prepend layout effect above never runs, so
  // clear the pending flag here to avoid suppressing the next bottom-follow.
  // Layout effects run before this, so a real prepend has already cleared it.
  useEffect(() => {
    if (!isLoadingOlder && isPrependingRef.current) {
      isPrependingRef.current = false;
    }
  }, [isLoadingOlder]);

  // Follow the bottom for genuinely new messages (sends/inbound). Keyed on the
  // NEWEST message id changing, so it never fires on the initial pin/reopen
  // (seeded above) or on an older-history prepend (newest unchanged). Gated by
  // stick-to-bottom so it won't yank a reader who has scrolled up.
  useEffect(() => {
    if (!opened || !didInitialScrollRef.current) return;
    const newestId = allMessages[allMessages.length - 1]?.id;
    if (!newestId || newestId === prevNewestIdRef.current) return;
    prevNewestIdRef.current = newestId;
    if (!stickToBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [allMessages, opened]);

  // Auto-load older history when the top sentinel scrolls into view.
  useEffect(() => {
    if (!opened || !hasMoreOlder) return;
    const viewport = viewportRef.current;
    const sentinel = topSentinelRef.current;
    if (!viewport || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadOlder();
        }
      },
      { root: viewport, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [opened, hasMoreOlder, handleLoadOlder]);

  // Determine if message is outbound (sent by any PractitionerRole)
  // PractitionerRoles are the only roles that can send outbound messages
  const isOutboundMessage = (communication: Communication): boolean => {
    const senderRef = communication.sender?.reference;
    return senderRef?.startsWith("PractitionerRole/") ?? false;
  };

  // Extract the conversation ID from the most recent message that actually
  // carries one. `allMessages` is ascending (oldest first), so scan from the
  // newest end backward: the newest row can be a phone call (or any
  // Communication without a `dialpad:conversationId`), which would otherwise
  // yield no id and drop threading even when earlier SMS rows carry the active
  // conversation. Using index 0 (oldest) would instead thread off stale history
  // and worsen as reverse pagination loads older pages.
  const conversationId = useMemo(() => {
    for (let i = allMessages.length - 1; i >= 0; i--) {
      const id = getConversationId(allMessages[i]);
      if (id) return id;
    }
    return undefined;
  }, [allMessages]);

  // Handle send message
  const handleSendMessage = async () => {
    if (!opened || sendInFlightRef.current || isSending || isComposing())
      return;
    if (placeholders.length > 0) {
      textareaRef.current?.focus();
      return;
    }
    if (!messageText.trim()) {
      notifications.show({
        title: "Message Required",
        message: "Please enter a message to send",
        color: "yellow",
        icon: <IconAlertCircle />,
      });
      return;
    }

    if (!hasValidRecipient) {
      notifications.show({
        title: "Phone Number Required",
        message: "Please select a phone number",
        color: "yellow",
        icon: <IconAlertCircle />,
      });
      return;
    }

    if (!hasValidSender) {
      notifications.show({
        title: "Clinician Phone Number Required",
        message: "Please select a phone number to send from",
        color: "yellow",
        icon: <IconAlertCircle />,
      });
      return;
    }

    // Snapshot only allow-listed operational attributes before crossing the async boundary.
    const senderCohort =
      !currentUser.practitioner?.id ||
      !currentUser.isRolesResolved ||
      currentUser.isLoading ||
      currentUser.error
        ? "unknown"
        : currentUser.roleCodes.some(
              (code) =>
                code === "special-operations-associate" ||
                code === "special-operations-director",
            )
          ? "special_ops"
          : "other";
    const sendContext = provenance
      ? {
          source: "template",
          templateKey: provenance.templateKey,
          catalogVersion: provenance.catalogVersion,
          edited: messageText !== provenance.renderedText,
          senderCohort,
        }
      : { source: "free_text", edited: false, senderCohort };
    sendInFlightRef.current = true;
    let optimisticCommunication: Communication;
    try {
      optimisticCommunication = await sendSMS({
        patientId,
        message: messageText,
        fromNumber: selectedFromNumber,
        toNumber: selectedToNumber,
        conversationId,
      });
    } catch (err) {
      showErrorNotification(
        err instanceof Error ? err.message : "Failed to send message",
        { title: "Send Failed" },
      );
      return;
    } finally {
      sendInFlightRef.current = false;
    }

    trackAction(TelemetryActions.SMSSendAccepted, sendContext);
    // Sending is an explicit intent to jump to the newest message, even if
    // the user had scrolled up reading history — re-enable bottom-follow so
    // the optimistic bubble scrolls into view.
    stickToBottomRef.current = true;

    // Add the optimistic message to local state for immediate display
    setOptimisticMessages((prev) => [...prev, optimisticCommunication]);

    // Extract the Dialpad messageId from the optimistic communication
    // This was returned from the send-sms API and stored in the identifier
    const messageId = optimisticCommunication.identifier?.find(
      (id: Identifier) => id.system === "dialpad:messageId",
    )?.value;

    // Start targeted polling for this specific message
    // This is much more efficient than polling for all messages!
    // - Queries exactly 1 message by identifier
    // - Polls every 2 seconds for up to 30 seconds
    if (messageId) {
      setPendingMessageId(messageId);
    }

    // Clear message input
    resetComposer();

    // No "sending…" toast: a 2xx response means Dialpad accepted the message,
    // so the optimistic bubble already renders as sent. The green "Message
    // Delivered" toast fires once the webhook-created record is reconciled.

    // Call optional callback with optimistic message
    onMessageSent?.(optimisticCommunication);
  };

  // Handle Enter key to send (with Shift+Enter for new line)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    textareaProps.onKeyDown(event);
    if (
      event.defaultPrevented ||
      isComposing() ||
      event.nativeEvent.isComposing ||
      event.nativeEvent.keyCode === 229
    )
      return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void handleSendMessage();
    }
  };

  const hasContextSection =
    phoneNumbers.length > 0 ||
    practitionerPhoneNumbers.length >= 1 ||
    Boolean(assignedClinicianRole?.practitioner?.display);

  return (
    <Stack gap="md" h="100%" className={styles.threadContent}>
      {/* Context section: phone selectors + assigned clinician */}
      {hasContextSection && (
        <Paper radius="md" p="sm" className={styles.contextPanel}>
          <Stack gap="sm">
            {/* Phone number selectors */}
            {(phoneNumbers.length > 0 ||
              practitionerPhoneNumbers.length > 1) && (
              <Group gap="md" align="flex-start" grow>
                {/* Patient phone number */}
                {phoneNumbers.length > 0 && (
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Text
                      size="xs"
                      fw={600}
                      className={styles.dimmedLabel}
                      tt="uppercase"
                      style={{ letterSpacing: "0.04em" }}
                    >
                      Send To
                    </Text>
                    <Select
                      value={selectedToNumber}
                      onChange={(val) => val && setSelectedToNumber(val)}
                      data={phoneNumbers}
                      leftSection={<IconPhone size={16} />}
                      placeholder="Select phone number"
                      size="sm"
                      disabled={
                        isLoadingPatient ||
                        isLoadingClinician ||
                        phoneNumbers.length === 0
                      }
                      styles={darkInputStyles}
                      classNames={{
                        dropdown: styles.darkSelectDropdown,
                        option: styles.darkSelectOption,
                      }}
                    />
                  </Stack>
                )}

                {/* Clinician phone number (dropdown only when multiple) */}
                {practitionerPhoneNumbers.length > 1 && (
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Text
                      size="xs"
                      fw={600}
                      className={styles.dimmedLabel}
                      tt="uppercase"
                      style={{ letterSpacing: "0.04em" }}
                    >
                      Send From
                    </Text>
                    <Select
                      value={selectedFromNumber}
                      onChange={(val) => val && setSelectedFromNumber(val)}
                      data={practitionerPhoneNumbers}
                      leftSection={<IconPhone size={16} />}
                      placeholder="Select clinician phone number"
                      size="sm"
                      disabled={isLoadingClinician}
                      styles={darkInputStyles}
                      classNames={{
                        dropdown: styles.darkSelectDropdown,
                        option: styles.darkSelectOption,
                      }}
                    />
                  </Stack>
                )}
              </Group>
            )}

            {/* Single clinician number — display as static text */}
            {practitionerPhoneNumbers.length === 1 && selectedFromNumber && (
              <Group gap="xs">
                <IconPhone size={14} color="rgba(255,255,255,0.55)" />
                <Text size="xs" className={styles.dimmedLabel}>
                  Sending from:{" "}
                  <Text span fw={600} className={styles.secondaryText}>
                    {selectedFromNumber}
                  </Text>
                </Text>
              </Group>
            )}

            {/* Assigned clinician badge */}
            {assignedClinicianRole?.practitioner?.display && (
              <Box className={styles.clinicianBadge}>
                <IconUser size={14} color="var(--drawer-dark-accent)" />
                <Text size="sm" className={styles.dimmedLabel}>
                  Assigned to:{" "}
                  <Text span fw={600} className={styles.accentStrongText}>
                    {assignedClinicianRole.practitioner.display}
                  </Text>
                </Text>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* Error States */}
      {error && (
        <Alert
          icon={<IconAlertCircle />}
          title="Error Loading Messages"
          radius="md"
          classNames={{
            root: styles.darkAlert,
            icon: styles.darkAlertIcon,
            title: styles.darkAlertTitle,
            message: styles.darkAlertMessage,
          }}
        >
          {error.message}
        </Alert>
      )}

      {sendError && (
        <Alert
          icon={<IconAlertCircle />}
          title="Send Error"
          radius="md"
          classNames={{
            root: styles.darkAlert,
            icon: styles.darkAlertIcon,
            title: styles.darkAlertTitle,
            message: styles.darkAlertMessage,
          }}
        >
          {sendError.message}
        </Alert>
      )}

      {/* Loading State */}
      {isLoadingMessages && (
        <Group justify="center" p="xl">
          <Loader size="md" color="purple.3" />
        </Group>
      )}

      {/* Message Thread */}
      <ScrollArea
        viewportRef={viewportRef}
        onScrollPositionChange={handleScrollPositionChange}
        type="auto"
        className={styles.messageHistory}
        pr="sm"
      >
        <Stack gap="xs">
          {allMessages.length === 0 && !isLoadingMessages ? (
            <Stack align="center" gap="md" py="xl">
              <Box className={styles.emptyStateIcon}>
                <IconMessage size={28} />
              </Box>
              <Stack gap={4} align="center">
                <Text fw={500} size="sm" className={styles.primaryText}>
                  No messages or calls yet
                </Text>
                <Text size="xs" className={styles.dimmedLabel} ta="center">
                  Send a message to start the conversation.
                </Text>
              </Stack>
            </Stack>
          ) : (
            <>
              {/* Sentinel: when it scrolls into view, load the next older batch */}
              <div ref={topSentinelRef} aria-hidden />
              {isLoadingOlder && (
                <Group
                  justify="center"
                  py="xs"
                  role="status"
                  aria-label="Loading older messages"
                >
                  <Loader size="sm" color="purple.3" />
                </Group>
              )}
              {allMessages.map((message) => {
                const isCall = isPhoneCall(message);
                const isOutbound = isOutboundMessage(message);

                return isCall ? (
                  <CallItem
                    key={message.id}
                    communication={message}
                    isOutbound={isOutbound}
                  />
                ) : (
                  <SMSMessage
                    key={message.id}
                    communication={message}
                    isOutbound={isOutbound}
                  />
                );
              })}
              {/* Invisible div for auto-scrolling */}
              <div ref={messagesEndRef} />
            </>
          )}
        </Stack>
      </ScrollArea>

      {/* Message Compose Area */}
      <Box className={styles.composer}>
        <Divider mb="md" color="rgba(255,255,255,0.15)" />
        <Textarea
          {...textareaProps}
          ref={textareaRef}
          aria-label="Message"
          aria-invalid={placeholders.length > 0}
          aria-describedby={
            [
              placeholders.length > 0 ? placeholderErrorId : undefined,
              isSurveyDraft ? surveyHintId : undefined,
            ]
              .filter(Boolean)
              .join(" ") || undefined
          }
          onKeyDown={handleKeyDown}
          placeholder="Type your message… (Shift+Enter for new line)"
          minRows={3}
          maxRows={6}
          autosize
          radius="md"
          disabled={isSending || !hasValidRecipient}
          styles={darkInputStyles}
        />
        {placeholders.length > 0 && (
          <Text
            id={placeholderErrorId}
            role="alert"
            size="xs"
            mt="xs"
            className={styles.placeholderError}
          >
            Replace {placeholders.join(" and ")} before sending.
          </Text>
        )}
        {isSurveyDraft && (
          <Text
            id={surveyHintId}
            size="xs"
            mt="xs"
            className={styles.secondaryText}
          >
            Add the survey link before sending.
          </Text>
        )}
        <Box
          role="group"
          aria-labelledby={templateLabelId}
          className={styles.templates}
        >
          <Text
            id={templateLabelId}
            size="xs"
            fw={600}
            className={styles.secondaryText}
          >
            Templates
          </Text>
          <div className={styles.templateButtons}>
            {SMS_TEMPLATES.map((template) => (
              <Button
                key={template.key}
                type="button"
                size="xs"
                variant="subtle"
                classNames={{
                  root: styles.templateButton,
                  label: styles.templateButtonLabel,
                }}
                disabled={
                  isSending ||
                  !hasValidRecipient ||
                  (template.key !== "scheduled" &&
                    isClinicianTemplateUnavailable)
                }
                onClick={() => selectTemplate(template.key)}
              >
                {template.label}
              </Button>
            ))}
            <ActionIcon
              disabled
              variant="subtle"
              aria-label="Template settings (coming soon)"
              className={styles.templateSettings}
            >
              <IconSettings size={16} aria-hidden />
            </ActionIcon>
          </div>
          {(isLoadingClinician || isLoadingClinicianName) && (
            <Text role="status" size="xs" className={styles.secondaryText}>
              Loading clinician details…
            </Text>
          )}
          {(clinicianError || clinicianNameError) && (
            <Group gap="xs" mt="xs">
              <Text role="status" size="xs" className={styles.placeholderError}>
                {clinicianError
                  ? "Clinician details could not be loaded."
                  : "Clinician name could not be loaded."}
              </Text>
              <Button
                size="xs"
                variant="subtle"
                className={styles.templateButton}
                onClick={() => void refetchClinician()}
              >
                Retry clinician lookup
              </Button>
            </Group>
          )}
        </Box>
        <Group justify="flex-end" mt="xs">
          <Button
            onClick={handleSendMessage}
            loading={isSending}
            disabled={
              !messageText.trim() ||
              placeholders.length > 0 ||
              !hasValidRecipient ||
              !hasValidSender
            }
            leftSection={<IconSend size={16} />}
            className={styles.sendButton}
          >
            Send Message
          </Button>
        </Group>
      </Box>
    </Stack>
  );
};

/**
 * Right-side drawer for viewing/sending SMS and viewing call history with
 * transcripts. Mounted unconditionally by ModalProvider so its slide animation
 * can play (Mantine's Transition only animates on an `opened` *update*, never
 * on mount/unmount). All patient data loading is deferred until the drawer is
 * first opened, and the per-patient body is keyed by patient so switching
 * patients starts from a clean slate.
 * @agentic connections
 */
export const SMSThreadDrawer = ({
  opened,
  onClose,
  patientId,
  onMessageSent,
}: SMSThreadDrawerProps) => {
  const { practitioner } = useCurrentUser();
  // The first resolved profile belongs to this mounted authenticated session.
  // Remember it without resetting a draft begun while the profile was loading.
  // Keep that identity through transient missing profiles; only a different
  // resolved account resets the body. Real logout unmounts this entire tree.
  const [senderSession, setSenderSession] = useState({
    senderId: practitioner?.id,
    generation: 0,
  });
  if (practitioner?.id && practitioner.id !== senderSession.senderId) {
    setSenderSession({
      senderId: practitioner.id,
      generation: senderSession.generation + (senderSession.senderId ? 1 : 0),
    });
  }
  // Advance the latch when the drawer opens for a (new) patient, and retain it
  // across close so the content stays rendered through the exit slide instead
  // of blanking mid-animation. Adjusting state during render — rather than in
  // an effect — is React's recommended pattern for deriving state from changing
  // props, and avoids a wasted commit + the set-state-in-effect lint rule.
  const [loadedPatientId, setLoadedPatientId] = useState<string | undefined>(
    undefined,
  );
  if (opened && patientId && patientId !== loadedPatientId) {
    setLoadedPatientId(patientId);
  }

  // Patient fetch drives the header title and is shared with the content so the
  // patient isn't fetched twice. Deferred until first open via loadedPatientId.
  const { data: patientData, isLoading: isLoadingPatient } =
    usePatientResource(loadedPatientId);
  const fetchedPatient = patientData as Patient | undefined;

  // The shared fetch (useApiResource) retains the PREVIOUS patient's data until
  // the newly-requested id resolves — so when the drawer is reused for a
  // different patient, `fetchedPatient` can briefly be the prior patient. The
  // body is keyed by `loadedPatientId` and would otherwise derive its "Send To"
  // recipient from that stale patient (latching the wrong phone number, which
  // its `!selectedToNumber` guard then never replaces). Only treat the fetched
  // patient as current once its id matches the patient we're loading, so the
  // header title and the recipient selector never reflect the prior patient.
  const patient =
    fetchedPatient && fetchedPatient.id === loadedPatientId
      ? fetchedPatient
      : undefined;
  const isPatientReady = Boolean(patient);

  return (
    <Drawer
      opened={opened && Boolean(patientId)}
      keepMounted
      onClose={onClose}
      size="lg"
      colorScheme="dark"
      icon={<IconMessage size={16} aria-hidden />}
      title={patient ? getPatientDisplayName(patient) : "SMS Messages"}
      padding="md"
    >
      {/* Keyed by patient so switching patients remounts a clean body —
          resetting compose fields, optimistic messages, and the thread rather
          than bleeding the previous patient's data (and its in-flight fetches)
          into the next. The Drawer shell stays mounted, so the slide animation
          is unaffected. */}
      {loadedPatientId ? (
        <SMSThreadContent
          key={`${loadedPatientId}:${senderSession.generation}`}
          patientId={loadedPatientId}
          opened={opened}
          patient={patient}
          isLoadingPatient={isLoadingPatient || !isPatientReady}
          onMessageSent={onMessageSent}
        />
      ) : null}
    </Drawer>
  );
};
